import React from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lightTheme, darkTheme } from './theme';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import UserProfile from './pages/UserProfile';
import CardsPage from './pages/CardsPage';
import CardDetailPage from './pages/CardDetailPage';
import EbayListingsPage from './pages/EbayListingsPage';
import BoxesPage from './pages/BoxesPage';
import SignupPage from './pages/SignupPage';
import AboutUsPage from './pages/AboutUsPage';
import LegalPage from './pages/LegalPage';
import TermsPrivacyPage from './pages/TermsPrivacyPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import SecretAdminLogin from './pages/SecretAdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AuthProvider from './contexts/AuthContext';
import AdminProvider from './contexts/AdminContext';
import { useAdmin } from './contexts/AdminContext';
import ThemeProvider, { useTheme } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import ErrorBoundary from './components/ErrorBoundary';
import './utils/cartUtils'; // Import cart utilities for global access

// Admin routes component that has access to AdminContext
const AdminRoutes: React.FC = () => {
  const { isAdmin } = useAdmin();
  
  return (
    <Routes>
      {/* Admin routes - deliberately obscured paths */}
      <Route path="/admin-yyd92c" element={<SecretAdminLogin />} />
      <Route 
        path="/management-portal/dashboard" 
        element={isAdmin ? <AdminDashboard /> : <Navigate to="/admin-yyd92c" replace />} 
      />
      
      {/* Standard routes with Header */}
      <Route path="/" element={
        <>
          <Header />
          <HomePage />
        </>
      } />
      <Route path="/search" element={
        <>
          <Header />
          <SearchPage />
        </>
      } />
      <Route path="/profile" element={
        <>
          <Header />
          <UserProfile />
        </>
      } />
      <Route path="/cards" element={
        <>
          <Header />
          <CardsPage />
        </>
      } />
      <Route path="/card/:id" element={
        <>
          <Header />
          <CardDetailPage />
        </>
      } />
      <Route path="/ebay-listings" element={
        <>
          <Header />
          <EbayListingsPage />
        </>
      } />
      <Route path="/boxes" element={
        <>
          <Header />
          <BoxesPage />
        </>
      } />
      <Route path="/signup" element={
        <>
          <Header />
          <SignupPage />
        </>
      } />
      <Route path="/about" element={
        <>
          <Header />
          <AboutUsPage />
        </>
      } />
      <Route path="/legal" element={
        <>
          <Header />
          <LegalPage />
        </>
      } />
      <Route path="/terms-privacy" element={
        <>
          <Header />
          <TermsPrivacyPage />
        </>
      } />
      <Route path="/cart" element={
        <>
          <Header />
          <CartPage />
        </>
      } />
      <Route path="/checkout" element={
        <>
          <Header />
          <CheckoutPage />
        </>
      } />
      <Route path="/checkout/success" element={
        <>
          <Header />
          <CheckoutSuccessPage />
        </>
      } />
    </Routes>
  );
};

// AppContent component that uses the theme context
const AppContent: React.FC = () => {
  const { mode } = useTheme();
  const currentTheme = mode === 'light' ? lightTheme : darkTheme;
  
  return (
    <MuiThemeProvider theme={currentTheme}>
      <CssBaseline />
      <AuthProvider>
        <AdminProvider>
          <CartProvider>
            <Router>
              <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
                <AdminRoutes />
              </Box>
            </Router>
          </CartProvider>
        </AdminProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
